import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableContainer,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";

const getCSRFTokenFromCookie = () => {
  const name = "csrftoken=";
  const parts = document.cookie.split(";").map((c) => c.trim());
  const hit = parts.find((p) => p.startsWith(name));
  return hit ? decodeURIComponent(hit.slice(name.length)) : "";
};

const ensureCsrf = async () => {
  let csrf = getCSRFTokenFromCookie();
  if (csrf) return csrf;
  await fetch("/sistema/ensure-csrf/", { method: "GET", credentials: "include" }).catch(() => {});
  csrf = getCSRFTokenFromCookie();
  if (csrf) return csrf;
  await fetch("/sistema/get-csrf-token/", { method: "GET", credentials: "include" }).catch(() => {});
  return getCSRFTokenFromCookie();
};

const onlyName = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  const i = s.indexOf(" ");
  return i === -1 ? s : s.slice(i + 1).trim();
};

const shortTipo = (v) => {
  const t = String(v || "").toUpperCase().trim();
  if (t === "AHORROS" || t === "AHORRO") return "AHORRO";
  if (t === "CORRIENTE") return "CORRIENTE";
  return t || "-";
};

const statusStyle = (activa) => ({
  fontSize: 12,
  fontWeight: 800,
  mt: 0.4,
  color: activa ? "#2e7d32" : "#d32f2f",
});

const AdminBank = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [bancos, setBancos] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [confirmDelete, setConfirmDelete] = useState({ open: false, banco: null });
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, type: "success", message: "" });

  const closeSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  const fetchBancos = async () => {
    setLoading(true);
    try {
      const r = await fetch("/cuentas/bancos/listar/", { method: "GET", credentials: "include" });
      const ct = r.headers.get("content-type") || "";
      const j = ct.includes("application/json") ? await r.json().catch(() => ({})) : {};
      if (!r.ok) throw new Error(j?.error || "No se pudieron cargar los bancos");
      setBancos(Array.isArray(j?.bancos) ? j.bancos : []);
    } catch (e) {
      setBancos([]);
      setSnackbar({ open: true, type: "error", message: e?.message || "No se pudieron cargar los bancos" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBancos();
  }, []);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return (bancos || []).slice(start, start + rowsPerPage);
  }, [bancos, page, rowsPerPage]);

  const handleDelete = async () => {
    const b = confirmDelete.banco;
    if (!b || deleting) return;

    setDeleting(true);
    try {
      const csrf = await ensureCsrf();

      const r = await fetch(`/cuentas/bancos/eliminar/${b.id}/`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          ...(csrf ? { "X-CSRFToken": csrf } : {}),
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const ct = r.headers.get("content-type") || "";
      const j = ct.includes("application/json") ? await r.json().catch(() => ({})) : {};

      if (!r.ok) throw new Error(j?.error || "No se pudo eliminar el banco");

      setBancos((prev) => (prev || []).filter((x) => x.id !== b.id));
      setSnackbar({ open: true, type: "success", message: "Banco eliminado correctamente." });
      setConfirmDelete({ open: false, banco: null });
    } catch (e) {
      setSnackbar({ open: true, type: "error", message: e?.message || "No se pudo eliminar el banco" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3, gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
              onClick={() => navigate("/admin-panel/bancos/nuevo")}
            >
              Agregar Cuenta Bancaria
            </Button>
          </Box>

          <Paper
            sx={{
              p: 3,
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              bgcolor: "#FFFFFF",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              Bancos
            </Typography>

            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Cuenta bancaria</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF", width: 150 }}>Número</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF", width: 120 }}>Tipo</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF", width: 160 }}>Ciudad</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Cuenta contable</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF", width: 180 }}>Banco</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF", width: 90 }} align="center">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={26} />
                      </TableCell>
                    </TableRow>
                  ) : paged.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: "#666" }}>
                        No hay cuentas bancarias registradas.
                      </TableCell>
                    </TableRow>
                  ) : (
                    paged.map((b) => {
                      const puedeEliminar = !b.tiene_movimientos;
                      return (
                        <TableRow key={b.id} hover>
                          <TableCell>
                            <Typography sx={{ fontWeight: 700, color: "#222" }}>{b.nombre}</Typography>
                            <Typography sx={statusStyle(!!b.activa)}>{b.activa ? "ACTIVA" : "INACTIVA"}</Typography>
                          </TableCell>

                          <TableCell>{b.numero_cuenta}</TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>{shortTipo(b.tipo_cuenta)}</TableCell>

                          <TableCell>{b.ciudad}</TableCell>

                          <TableCell>{onlyName(b.cuenta_contable_nombre)}</TableCell>

                          <TableCell>{String(b.banco || "").toUpperCase()}</TableCell>

                          <TableCell align="center">
                            <IconButton
                              size="small"
                              disabled={!puedeEliminar}
                              onClick={() => setConfirmDelete({ open: true, banco: b })}
                              sx={{
                                color: puedeEliminar ? "#d32f2f" : "#bdbdbd",
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={(bancos || []).length}
              page={page}
              onPageChange={(_, n) => setPage(n)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={confirmDelete.open}
        onClose={() => (deleting ? null : setConfirmDelete({ open: false, banco: null }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900, color: "#d32f2f" }}>Eliminar banco</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            ¿Seguro deseas eliminar la cuenta bancaria <b>{confirmDelete.banco?.nombre || ""}</b>?
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 12, color: "#757575" }}>
            Solo se puede eliminar si no registra movimientos contables.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmDelete({ open: false, banco: null })}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ bgcolor: "#d32f2f", "&:hover": { bgcolor: "#b71c1c" }, textTransform: "none" }}
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.type} onClose={closeSnackbar} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminBank;
